import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, Download, FileDown, Loader2, CheckCircle2, Clock, CircleSlash } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function BackupBadge({ s }) {
  if (s === "backed_up") return <span className="inline-flex items-center gap-1 text-xs bg-[#5C8A72]/15 text-[#5C8A72] px-2.5 py-1 rounded-full" data-testid="backup-badge-ok"><CheckCircle2 size={12} /> Backed up</span>;
  if (s === "error") return <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-600 px-2.5 py-1 rounded-full" data-testid="backup-badge-error"><CircleSlash size={12} /> Failed</span>;
  return <span className="inline-flex items-center gap-1 text-xs bg-[#D98E73]/15 text-[#B5794E] px-2.5 py-1 rounded-full" data-testid="backup-badge-pending"><Clock size={12} /> Pending</span>;
}

export default function SignaturesAudit() {
  const [downloading, setDownloading] = useState(false);
  const { data: sigs = [], isLoading } = useQuery({
    queryKey: ["admin-signatures"],
    queryFn: () => api.get("/agreements/signatures/all").then((r) => r.data),
  });

  const authDownload = async (path, filename) => {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportZip = async () => {
    setDownloading(true);
    try { await authDownload("/admin/agreements/signatures/export.zip", "EYW-signed-agreements.zip"); toast.success("Export downloaded"); }
    catch (e) { toast.error(formatApiError(e.message)); }
    finally { setDownloading(false); }
  };

  const downloadOne = async (s) => {
    try { await authDownload(`/me/signatures/${s.id}/pdf`, `${(s.agreement_title || "agreement").replace(/[^a-z0-9]+/gi, "-")}.pdf`); }
    catch (e) { toast.error(formatApiError(e.message)); }
  };

  return (
    <div data-testid="signatures-audit">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><ShieldCheck size={20} className="text-[#144F75]" /> Signed agreements — audit log</h2>
          <p className="text-xs text-[#4A5D6B] mt-1">Every signature is stored as a PDF (full text + signer, signature, version, date &amp; IP) and backed up to Google Drive when connected.</p>
        </div>
        <button onClick={exportZip} disabled={downloading || sigs.length === 0} data-testid="signatures-export-zip" className="eyw-btn !py-2 text-sm disabled:opacity-60">
          {downloading ? <Loader2 className="animate-spin" size={16} /> : <FileDown size={16} />} Download all (ZIP)
        </button>
      </div>

      {isLoading ? (
        <div className="grid place-items-center py-14"><Loader2 className="animate-spin text-[#144F75]" size={24} /></div>
      ) : sigs.length === 0 ? (
        <p className="text-sm text-[#4A5D6B]">No signed agreements yet.</p>
      ) : (
        <>
        <div className="overflow-x-auto eyw-card hidden md:block">
          <table className="w-full text-sm" data-testid="signatures-table">
            <thead>
              <tr className="text-left text-[#8FA89B] border-b border-[#E5E0D8]">
                <th className="px-4 py-3 font-medium">Signer</th>
                <th className="px-4 py-3 font-medium">Agreement</th>
                <th className="px-4 py-3 font-medium">Ver.</th>
                <th className="px-4 py-3 font-medium">Date signed (UTC)</th>
                <th className="px-4 py-3 font-medium">IP</th>
                <th className="px-4 py-3 font-medium">Backup</th>
                <th className="px-4 py-3 font-medium text-right">PDF</th>
              </tr>
            </thead>
            <tbody>
              {sigs.map((s) => (
                <tr key={s.id} className="border-b border-[#E5E0D8]/60 hover:bg-[#144F75]/[0.02]" data-testid={`signature-row-${s.id}`}>
                  <td className="px-4 py-3">
                    <div className="text-[#1A242B]">{s.name || "-"}</div>
                    <div className="text-xs text-[#8FA89B]">{s.user_email}</div>
                  </td>
                  <td className="px-4 py-3 text-[#4A5D6B]">{s.agreement_title}</td>
                  <td className="px-4 py-3 text-[#4A5D6B]">v{s.agreement_version}</td>
                  <td className="px-4 py-3 text-[#4A5D6B] whitespace-nowrap">{(s.signed_at || "").replace("T", " ").slice(0, 16)}</td>
                  <td className="px-4 py-3 text-[#8FA89B]">{s.ip || "-"}</td>
                  <td className="px-4 py-3">
                    {s.drive_link ? <a href={s.drive_link} target="_blank" rel="noreferrer"><BackupBadge s={s.drive_status} /></a> : <BackupBadge s={s.drive_status} />}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => downloadOne(s)} data-testid={`signature-pdf-${s.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Download size={13} /> PDF</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="md:hidden space-y-3">
          {sigs.map((s) => (
            <div key={s.id} data-testid={`signature-card-${s.id}`} className="eyw-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[#1A242B] font-medium break-words">{s.name || "-"}</p>
                  <p className="text-xs text-[#8FA89B] break-all">{s.user_email}</p>
                </div>
                <button onClick={() => downloadOne(s)} data-testid={`signature-pdf-${s.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs shrink-0"><Download size={13} /> PDF</button>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                <div><dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">Agreement</dt><dd className="text-[#4A5D6B] break-words">{s.agreement_title} <span className="text-[#8FA89B]">v{s.agreement_version}</span></dd></div>
                <div><dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">Signed (UTC)</dt><dd className="text-[#4A5D6B]">{(s.signed_at || "").replace("T", " ").slice(0, 16)}</dd></div>
                <div><dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">IP</dt><dd className="text-[#8FA89B]">{s.ip || "-"}</dd></div>
                <div><dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">Backup</dt><dd>{s.drive_link ? <a href={s.drive_link} target="_blank" rel="noreferrer"><BackupBadge s={s.drive_status} /></a> : <BackupBadge s={s.drive_status} />}</dd></div>
              </dl>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
}
